import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, EyeOff, Save } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'sonner';

const SectionsManager = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({
    section_key: '',
    heading: '',
    subheading: '',
    description: '',
    media_url: '',
    media_type: '',
    cta_text: '',
    cta_link: '',
    visible: true,
    order: 0,
  });

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cms/sections`
      );
      const data = await response.json();
      setSections(data);
    } catch (error) {
      toast.error('Failed to load sections');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('cms_token');

    try {
      const url = editingSection
        ? `${process.env.REACT_APP_BACKEND_URL}/api/cms/sections/${editingSection.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/cms/sections`;

      const method = editingSection ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to save section');

      toast.success(
        editingSection ? 'Section updated successfully' : 'Section created successfully'
      );
      setEditingSection(null);
      setFormData({
        section_key: '',
        heading: '',
        subheading: '',
        description: '',
        media_url: '',
        media_type: '',
        cta_text: '',
        cta_link: '',
        visible: true,
        order: 0,
      });
      loadSections();
    } catch (error) {
      toast.error('Failed to save section');
    }
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setFormData({
      section_key: section.section_key,
      heading: section.heading,
      subheading: section.subheading,
      description: section.description,
      media_url: section.media_url || '',
      media_type: section.media_type || '',
      cta_text: section.cta_text || '',
      cta_link: section.cta_link || '',
      visible: section.visible,
      order: section.order,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;

    const token = localStorage.getItem('cms_token');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cms/sections/${id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to delete section');

      toast.success('Section deleted successfully');
      loadSections();
    } catch (error) {
      toast.error('Failed to delete section');
    }
  };

  const handleToggleVisibility = async (section) => {
    const token = localStorage.getItem('cms_token');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cms/sections/${section.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ visible: !section.visible }),
        }
      );

      if (!response.ok) throw new Error('Failed to update visibility');

      toast.success('Visibility updated');
      loadSections();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-purple-500">Loading sections...</div>;
  }

  return (
    <div data-testid="sections-manager">
      <div className="mb-8">
        <h1 className="text-4xl font-black mb-2">Section Manager</h1>
        <p className="text-gray-400">Create and manage website sections</p>
      </div>

      {/* Section Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8"
      >
        <h2 className="text-2xl font-bold mb-6">
          {editingSection ? 'Edit Section' : 'Create New Section'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold mb-2">Section Key *</label>
              <input
                type="text"
                required
                value={formData.section_key}
                onChange={(e) => setFormData({ ...formData, section_key: e.target.value })}
                disabled={!!editingSection}
                placeholder="e.g., hero, about, services"
                className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-2">Order</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">Heading</label>
            <input
              type="text"
              value={formData.heading}
              onChange={(e) => setFormData({ ...formData, heading: e.target.value })}
              placeholder="Main heading"
              className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">Subheading</label>
            <input
              type="text"
              value={formData.subheading}
              onChange={(e) => setFormData({ ...formData, subheading: e.target.value })}
              placeholder="Subheading"
              className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">Description (Rich Text)</label>
            <div className="bg-white rounded-lg">
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                placeholder="Enter description with rich text formatting..."
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link'],
                    ['clean'],
                  ],
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold mb-2">Media URL</label>
              <input
                type="text"
                value={formData.media_url}
                onChange={(e) => setFormData({ ...formData, media_url: e.target.value })}
                placeholder="https://..."
                className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-2">Media Type</label>
              <select
                value={formData.media_type}
                onChange={(e) => setFormData({ ...formData, media_type: e.target.value })}
                className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
              >
                <option value="">None</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold mb-2">CTA Button Text</label>
              <input
                type="text"
                value={formData.cta_text}
                onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                placeholder="Get Started"
                className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-2">CTA Button Link</label>
              <input
                type="text"
                value={formData.cta_link}
                onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                placeholder="/contact"
                className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.visible}
                onChange={(e) => setFormData({ ...formData, visible: e.target.checked })}
                className="w-5 h-5"
              />
              <span className="text-sm font-bold">Visible on website</span>
            </label>
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              data-testid="save-section-btn"
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-bold transition-all flex items-center gap-2"
            >
              <Save size={20} />
              {editingSection ? 'Update Section' : 'Create Section'}
            </button>
            {editingSection && (
              <button
                type="button"
                onClick={() => {
                  setEditingSection(null);
                  setFormData({
                    section_key: '',
                    heading: '',
                    subheading: '',
                    description: '',
                    media_url: '',
                    media_type: '',
                    cta_text: '',
                    cta_link: '',
                    visible: true,
                    order: 0,
                  });
                }}
                className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-bold transition-all"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </motion.div>

      {/* Sections List */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">All Sections ({sections.length})</h2>
        {sections.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
            <p className="text-gray-400 mb-4">No sections yet</p>
            <p className="text-sm text-gray-500">Create your first section using the form above</p>
          </div>
        ) : (
          sections.map((section) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-purple-500/30 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold">{section.heading || 'Untitled Section'}</h3>
                    <span
                      className={`text-xs px-3 py-1 rounded-full ${
                        section.visible
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}
                    >
                      {section.visible ? 'Visible' : 'Hidden'}
                    </span>
                    <span className="text-xs px-3 py-1 rounded-full bg-purple-500/20 text-purple-400">
                      {section.section_key}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mb-2">{section.subheading}</p>
                  {section.description && (
                    <div
                      className="text-sm text-gray-500 line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: section.description }}
                    />
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleToggleVisibility(section)}
                    data-testid={`toggle-visibility-${section.id}`}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title={section.visible ? 'Hide' : 'Show'}
                  >
                    {section.visible ? <Eye size={20} /> : <EyeOff size={20} />}
                  </button>
                  <button
                    onClick={() => handleEdit(section)}
                    data-testid={`edit-section-${section.id}`}
                    className="p-2 hover:bg-purple-500/20 text-purple-500 rounded-lg transition-colors"
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(section.id)}
                    data-testid={`delete-section-${section.id}`}
                    className="p-2 hover:bg-red-500/20 text-red-500 rounded-lg transition-colors"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default SectionsManager;